"use client";
import Image from "next/image";
import { Close } from "../common/Icons";
import { useAuth } from "@/hook/useAuth";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import Link from "next/link";
import { body } from "framer-motion/client";

const SmallNavBar = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const [open, setOpen] = useState(false);
  const { isAnonymous } = useAuth();
  const navBarCloseHandler = () => {
    setIsOpen(false);
    document.body.classList.remove("!overflow-hidden");
  };
  return (
    <div
      className={`md:w-[40%] w-[90%] bg-white lg:hidden rounded-l-[64px] pt-[35px] pb-[48px] duration-300 ease-in-out fixed top-0 z-[99] h-[100dvh] shadow-[0px_12px_16px_2px_#5B57EB26] overflow-y-auto ${isOpen ? "right-0" : "-right-full"
        }`}
    >
      <div className="flex items-end justify-between ps-[24px] pe-[32px]">
        <div className="max-w-[150px] betaRibbon">
          <Image
            priority
            className="w-full h-full"
            src="/logo/png/sideLogoDark.png"
            alt="logo"
            width={135}
            height={30}
          />
        </div>
        <span
          onClick={navBarCloseHandler}
          className="inline-block cursor-pointer"
        >
          <Close />
        </span>
      </div>
      <div className="m-7">
        {isAnonymous && (
          <div className="bg-[#EBE5FE] rounded-[24px] shadow-[0px_4px_20px_0px_#5E57EB33] py-6 px-3.5 flex flex-col items-center justify-center">
            <p className="ff_poppins text-lg text-center">Access Your World</p>
            <p className="text-sm ff_poppins text-center">
              Your future is just a step away!
            </p>
            <Link
              href="/auth"
              onClick={() => document.body.classList.remove("!overflow-hidden")}
              className="bg-primary-gradient text-white font-semibold text-sm ff_poppins rounded-full mt-3 py-2 px-[22px]"
            >
              Get Started Free
            </Link>
          </div>
        )}

        <div className="pt-6 mt-6 border-t border-t-[#D4D4D4] flex  flex-col gap-4">
          <Link
            href="/all-countries"
            onClick={() => document.body.classList.remove("!overflow-hidden")}
            className="rounded-xl border border-[#E0E0FF] bg-[linear-gradient(90deg,_#F0F4FF_0%,_#F1F4FF_12.5%,_#F2F3FF_25%,_#F3F3FF_37.5%,#F4F2FF_50%,_#F5F2FF_62.5%,_#F6F1FF_75%,_#F7F1FF_87.5%,_#F8F0FF_100%)] flex items-center gap-3 py-3 px-[13px]"
          >
            <Image
              className="max-w-[24px] max-h-[24px] w-full h-full"
              width={24}
              height={24}
              src="/assets/images/png/homepage/navGlobeBook.png"
              alt="bookGlobe"
            />
            <p className="ff_poppins font-medium text-lg text-[#5A4D9E]">
              Study Global
            </p>
          </Link>
          <Link
            href="/chat"
            onClick={() => document.body.classList.remove("!overflow-hidden")}
            className="rounded-xl border border-[#FFE0F0] bg-[linear-gradient(90deg,_#FFF4F0_0%,_#FFF4F2_10%,_#FFF3F3_20%,_#FFF3F5_30%,#FFF2F6_40%,_#FEF2F8_50%,_#FEF2F9_60%,_#FEF1FB_70%,_#FEF1FC_80%,_#FEF0FE_90%,_#FEF0FF_100%)] flex items-center gap-3 py-3 px-[13px]"
          >
            <Image
              className="max-w-[24px] max-h-[24px] w-full h-full"
              width={24}
              height={24}
              src="/assets/images/png/homepage/chatBot.png"
              alt="chatBot"
            />
            <p className="ff_poppins font-medium text-lg text-[#9E5D8A]">
              EdviaBot
            </p>
          </Link>

          <div
            onClick={() => setOpen(!open)}
            className="rounded-xl border border-[#FFE0F0] bg-[linear-gradient(90deg,_#F5F5F5_0%,_#F6F5F6_14.29%,_#F8F5F6_28.57%,_#F9F5F7_42.86%,_#FBF5F8_57.14%,_#FCF5F9_71.43%,_#FEF5F9_85.71%,_#FFF5FA_100%)] flex items-center justify-between gap-3 py-3 px-[13px]"
          >
            <div className="flex items-center gap-3">
              <Image
                className="max-w-[24px] max-h-[24px] w-full h-full"
                width={24}
                height={24}
                src="/assets/images/png/homepage/members.png"
                alt="chatBot"
              />
              <p className="ff_poppins font-medium text-lg text-[#6C6C74]">
                Company
              </p>
            </div>
            <ChevronUp
              className={`text-[#6C6C74] duration-300 ease-in-out ${open ? "rotate-0" : "rotate-180"}`}
            />
          </div>
          <div
            className={`mt-3 ${open ? "opacity-100 block" : "opacity-0 hidden"}`}
          >
            <div className="flex flex-col gap-1.5">
              <Link
                href="/about-us"
                onClick={() =>
                  document.body.classList.remove("!overflow-hidden")
                }
                className="ff_poppins text-base text-[#6C6C74] flex items-center justify-start gap-2 py-2.5 px-5"
              >
                <span className="size-1.5 bg-[#D0C0F0] rounded-full"></span>
                About Us
              </Link>
              <Link
                href="/for-institutions"
                onClick={() =>
                  document.body.classList.remove("!overflow-hidden")
                }
                className="ff_poppins text-base text-[#6C6C74] flex items-center justify-start gap-2 py-2.5 px-5"
              >
                <span className="size-1.5 bg-[#D0C0F0] rounded-full"></span>
                For institutions
              </Link>
              <Link
                href="/careers"
                onClick={() =>
                  document.body.classList.remove("!overflow-hidden")
                }
                className="ff_poppins text-base text-[#6C6C74] flex items-center justify-start gap-2 py-2.5 px-5"
              >
                <span className="size-1.5 bg-[#D0C0F0] rounded-full"></span>
                Careers
              </Link>
              <Link
                href="/latest-news"
                onClick={() =>
                  document.body.classList.remove("!overflow-hidden")
                }
                className="ff_poppins text-base text-[#6C6C74] flex items-center justify-start gap-2 py-2.5 px-5"
              >
                <span className="size-1.5 bg-[#D0C0F0] rounded-full"></span>
                News
              </Link>
              <Link
                href="/blogs?page=1"
                onClick={() =>
                  document.body.classList.remove("!overflow-hidden")
                }
                className="ff_poppins text-base text-[#6C6C74] flex items-center justify-start gap-2 py-2.5 px-5"
              >
                <span className="size-1.5 bg-[#D0C0F0] rounded-full"></span>
                Blogs
              </Link>
            </div>
          </div>
          <Link
            href="/book-exam"
            className="rounded-xl border border-[#FFE0F0] bg-[linear-gradient(90deg,_#FFF4F0_0%,_#FFF4F2_10%,_#FFF3F3_20%,_#FFF3F5_30%,#FFF2F6_40%,_#FEF2F8_50%,_#FEF2F9_60%,_#FEF1FB_70%,_#FEF1FC_80%,_#FEF0FE_90%,_#FEF0FF_100%)] flex items-center gap-3 py-3 px-[13px]"
          >
            <Image
              className="max-w-[24px] max-h-[24px] w-full h-full"
              width={24}
              height={24}
              src="/assets/images/png/homepage/search-programs.png"
              alt="search-programs"
            />
            <p className="ff_poppins font-medium text-lg text-[#9E5D8A]">
              Book Exam
            </p>
          </Link>
        </div>
      </div>
    </div>
  );
};
export default SmallNavBar;
